import { s } from "./builders";
import { IndexKeys, TupleProperties } from "./utils";


/**
 * Any kind of shape.
 * 
 * > This type should really only be used
 * > by functions that operate on _nonspecific_
 * > shapes (like a generic function that
 * > serialized a shape).
 * > 
 * > Regular users should use concrete
 * > implementations of shapes - like the ones
 * > created by the `def()` function!
 */
export type AnyShape = Shape.Any;
  

/**
 * Types that can be represented by a single, simple, shape
 * 
 * (for some reason, includes null???)
 */
export type SimpleShapeableType =
  | string
  | number
  | boolean
  | null

export type CompoundShapableType =
  | { readonly [key: string]: ShapableType }
  | ReadonlyArray<ShapableType>

/**
 * Any kind of type that could be represented by a
 * shape.
 * 
 * > Much like "AnyShape", you should be using concrete
 * > types in most day-to-day functions - this type
 * > is typically used for functions that operate on
 * > the generic results of shapes, or as an `extends` requirement
 * > to make sure the type that is being passed _can_ be converted
 * > to a shape.
 */
export type ShapableType =
  | SimpleShapeableType
  | CompoundShapableType

export namespace Shape {
  export namespace Compound {
    export type Object = {
      readonly type: 'object',
      readonly properties: { [key: string]: Any },
    };
    export type Array = {
      readonly type: 'array',
      readonly element: AnyShape,
    }
    export type Literal = {
      readonly type: 'literal',
      readonly value: SimpleShapeableType
    }
    export type Tuple = {
      readonly type: 'tuple',
      readonly elements: ReadonlyArray<AnyShape>
    }
  }
  export namespace Simple {
    export type String = {
      readonly type: 'string'
    }
    export type Number = {
      readonly type: 'number'
    }
    export type Boolean = {
      readonly type: 'boolean'
    }
  }
  export namespace Complex {
    export type Union = {
      readonly type: 'union',
      readonly of: ReadonlyArray<Shape>
    }
    export type Meta = {
      readonly type: 'meta',
      readonly properties: Record<string, string>
      readonly inner: Shape
    }
    /**
     * This shape is actually impossible to receive
     * at runtime, but captures a shape that specifically
     * resembles a typescript type (where the underlying shape
     * type is compatible with the type).
     * 
     * Acquired via the `def()` function.
     */
    export type Ref<T extends Type.Any = Type.Any> = { type: 'ref', __ref: T, real: Shape };

    export type Unknown = {
      readonly type: 'unknown'
    }
  }

  export type Simple = 
    | Simple.Boolean
    | Simple.String
    | Simple.Number

  export type Compound = 
    | Compound.Object
    | Compound.Array
    | Compound.Literal
    | Compound.Tuple

  export type Complex = 
    | Complex.Union
    | Complex.Ref
    | Complex.Unknown
    | Complex.Meta

  export type Any =
    | Simple
    | Compound
    | Complex

  export namespace Type {
    export type Simple =
      | string
      | number
      | boolean
      | null

    export type Compound =
      | { [key: string]: Shape.Type.Any }
      | Array<Shape.Type.Any>

    export type Any =
      | Simple
      | Compound

    type AsShape<T> = T extends Shape.Any ? T : never;

    type OfArray<T extends Compound.Array> = (Of<T["element"]>)[];

    type OfTuple<T extends Compound.Tuple> = TupleProperties<{
      -readonly [Index in IndexKeys<T["elements"]>]: Of<AsShape<T["elements"][Index]>>
    }, T["elements"]>
    
    type OfObject<T extends Compound.Object> = {
      -readonly [key in keyof T["properties"]]: Of<T["properties"][key]>
    }

    type OfUnion<T extends Complex.Union> = Of<T["of"][number]>

    export type Of<T extends Shape.Any> =
      Shape.Any extends T ? Shape.Type.Any
      : (
        | (T extends Simple.String ? string : never)
        | (T extends Simple.Number ? number : never)
        | (T extends Simple.Boolean ? boolean: never)
        | (T extends Compound.Array ? OfArray<T> : never)
        | (T extends Compound.Object ? OfObject<T> : never)
        | (T extends Compound.Literal ? T["value"] : never)
        | (T extends Compound.Tuple ? OfTuple<T> : never)
        | (T extends Complex.Union ? OfUnion<T> : never)
        | (T extends Complex.Ref<infer X> ? X : never)
        | (T extends Complex.Unknown ? unknown : never)
        | (T extends Complex.Meta ? Of<T["inner"]> : never)
      )
  }

  export type Matches<Target, Test extends Shape.Any> =
    Shape.Type.Of<Test> extends Target ? true : false
}
export type Shape = Shape.Any;
export const Shape = s;

/**
 * Define a Shape.
 * 
 * This function does nothing, but will throw a type
 * error if the provided object is not a shape - helping
 * your find type errors earlier on rather than later
 * when using the shape.
 */
export const def = <Type extends Shape.Type.Any, Force = false>() =>
  <TShape extends Shape.Any>(x: TShape):
    Force extends "force" ? Shape.Complex.Ref<Type>
    : Shape.Matches<Type, TShape> extends true
      ? Shape.Complex.Ref<Type>
      : StaticTypeError<"Shape does not meet Type requirements"> =>
    x as any;

export type StaticTypeError<Message> = Message & { __error: true };
